/* eslint-disable complexity */
/* eslint-disable camelcase */
'use strict';

import {
  BlendSupplier,
  GetSupplierBlendInterface,
  SupplierQuantity,
} from '../../../shared/interfaces/survey.interface';

export interface AllocationInterface {
  quantity(id: number): number;
  distribute(): void;
}

class Supplier {
  suppliers: { id: number }[];
  constructor(
    public readonly id: number,
    public quantity = 0,
    public group_name = '',
    public readonly flexValue = 100
  ) {
    this.suppliers = [{ id }];
    this.quantity = quantity;
    this.group_name = group_name;
    this.flexValue = flexValue;
  }

  incrementQuantity(value: number) {
    this.quantity += value;
  }
}

class KeepAllocation implements AllocationInterface {
  suppliers: Supplier[];
  aditionalQuantity: number;
  constructor(suppliers: SupplierQuantity[], aditionalQuantity: number) {
    const suppliersPrepared = [];
    for (let index = 0; index < suppliers.length; index++) {
      const supplier = suppliers[index];
      let quantity = supplier.quantity;
      suppliersPrepared.push(new Supplier(supplier.id, quantity));
    }
    this.suppliers = suppliersPrepared;
    this.aditionalQuantity = aditionalQuantity;
  }

  distribute(): void {
    const allocationQty = 1;
    while (this.aditionalQuantity > 0) {
      for (let index = 0; index < this.suppliers.length; index++) {
        const supplier = this.suppliers[index];
        if (this.aditionalQuantity > 0 && supplier.quantity != 0) {
          supplier.quantity = supplier.quantity + allocationQty;
          this.aditionalQuantity = this.aditionalQuantity - allocationQty;
        }
      }
    }
  }

  quantity(id: number): number {
    return new Quantity(this.suppliers, id).value();
  }
}

class EqualAllocation implements AllocationInterface {
  suppliers: Supplier[];
  constructor(
    suppliers: SupplierQuantity[],
    public readonly completes: number
  ) {
    const suppliersPrepared = [];
    for (let index = 0; index < suppliers.length; index++) {
      const supplier = suppliers[index];
      let quantity = 0;
      suppliersPrepared.push(new Supplier(supplier.id, quantity));
    }
    this.suppliers = suppliersPrepared;
    this.completes = completes;
  }

  distribute() {
    for (let index = 0; index < this.suppliers.length; index++) {
      const supplier = this.suppliers[index];
      supplier.quantity = 0;
    }
    let remainingCompletes = this.completes;
    const allocationQty = 1;
    while (remainingCompletes > 0) {
      for (let index = 0; index < this.suppliers.length; index++) {
        const supplier = this.suppliers[index];
        if (remainingCompletes > 0) {
          supplier.quantity = supplier.quantity + allocationQty;
          remainingCompletes = remainingCompletes - allocationQty;
        }
      }
    }
  }

  quantity(id: number) {
    return new Quantity(this.suppliers, id).value();
  }
}

class BlendAllocation implements AllocationInterface {
  suppliers: Supplier[];
  aditionalQuantity: number;
  constructor(
    suppliers: SupplierQuantity[],
    public readonly completes: number,
    public readonly blend: GetSupplierBlendInterface,
    aditionalQuantity: number
  ) {
    const suppliersPrepared = [];
    for (let index = 0; index < suppliers.length; index++) {
      const supplier = suppliers[index];
      let quantity = 0;
      suppliersPrepared.push(new Supplier(supplier.id, quantity));
    }
    this.suppliers = suppliersPrepared;
    this.completes = completes;
    this.blend = blend;
    this.aditionalQuantity = aditionalQuantity;
  }

  distribute() {
    const allocation = new Blend(this.blend, this.suppliers, this.completes);

    const suppliers = allocation.mapGroupForSrvSuppliers(
      this.suppliers,
      this.blend
    );
    const mappedSuppliers = allocation.rearrangeSuppliersAsPerGroup(suppliers);

    const finalSupplier = allocation.applyBlendAllocation(
      mappedSuppliers,
      this.blend,
      this.completes
    );
    this.suppliers = finalSupplier;
    const allocationQty = 1;
    while (this.aditionalQuantity > 0) {
      for (let index = 0; index < this.suppliers.length; index++) {
        const supplier = this.suppliers[index];
        if (this.aditionalQuantity > 0 && supplier.quantity != 0) {
          supplier.quantity = supplier.quantity + allocationQty;
          this.aditionalQuantity = this.aditionalQuantity - allocationQty;
        }
      }
    }
    return finalSupplier;
  }

  quantity(id: number) {
    return new Quantity(this.suppliers, id).value();
  }
}

class Quantity {
  constructor(
    public readonly suppliers: Supplier[],
    public readonly id: number
  ) {
    this.suppliers = suppliers;
    this.id = id;
  }

  value() {
    if (
      this.suppliers.find((supplier: Supplier) =>
        supplier.suppliers?.map((supp) => supp.id).includes(this.id)
      )
    ) {
      const quantity = this.suppliers.find((supplier) =>
        supplier.suppliers?.map((supp) => supp.id).includes(this.id)
      );
      if (quantity) {
        return quantity.quantity;
      }
      return 0;
    }
    return 0;
  }
}

export class AllocationFactory {
  static allocation(
    suppliers: SupplierQuantity[],
    completes: number,
    blend: GetSupplierBlendInterface,
    hasSupplierWithoutQuantity: boolean,
    aditionalQuantity: number
  ) {
    let allocation: AllocationInterface;
    if (blend.id != '') {
      allocation = new BlendAllocation(
        suppliers,
        completes,
        blend,
        aditionalQuantity
      );
    } else if (hasSupplierWithoutQuantity) {
      allocation = new EqualAllocation(suppliers, completes);
    } else {
      allocation = new KeepAllocation(suppliers, aditionalQuantity);
    }
    allocation.distribute();
    return allocation;
  }

  constructor() {}
}

export class Flexibility {
  constructor(
    public readonly flexValue: number,
    public readonly allocation: number,
    public readonly completesNeeded: number
  ) {
    this.flexValue = flexValue;
    this.allocation = allocation;
    this.completesNeeded = completesNeeded;
  }

  goal(): number {
    let completes_goal = 0;
    if (!this.flexValue) {
      completes_goal = this.allocation;
    } else if (this.flexValue >= 100) {
      completes_goal = this.completesNeeded;
    } else {
      const max = this.allocation + (this.allocation * this.flexValue) / 100;
      completes_goal = Math.floor(Math.min(max, this.completesNeeded));
    }
    return completes_goal;
  }
}

class Blend {
  constructor(
    public readonly blend: GetSupplierBlendInterface,
    public readonly suppliers: Supplier[],
    public readonly completes: number
  ) {
    this.blend = blend;
    this.suppliers = suppliers;
    this.completes = completes;
  }

  mapGroupForSrvSuppliers(
    suppliers: Supplier[],
    blend: GetSupplierBlendInterface
  ) {
    const suppliersGrouped = suppliers.map((srvSup) => {
      const group = blend.suppliers.filter(
        (sup: BlendSupplier) =>
          sup.supplier_id.map((supplier) => supplier.id).includes(srvSup.id) &&
          sup.group_name !== ''
      );
      if (group.length) {
        srvSup.group_name = group[0].group_name || '';
      }
      return srvSup;
    });
    return suppliersGrouped;
  }

  rearrangeSuppliersAsPerGroup(suppliers: Supplier[]) {
    const mappedSuppliers: Supplier[] = [];
    for (let i = 0; i < suppliers.length; i++) {
      const srvSup = suppliers[i];
      const index = mappedSuppliers.findIndex(
        (sup) => srvSup.group_name && sup.group_name === srvSup.group_name
      );
      const subSupplier: { id: number } = { id: srvSup.id };
      if (index !== -1) {
        mappedSuppliers[index].suppliers.push(subSupplier);
        mappedSuppliers[index].quantity =
          Number(mappedSuppliers[index].quantity) + Number(srvSup.quantity);
      } else {
        mappedSuppliers.push(
          new Supplier(
            subSupplier.id,
            srvSup.quantity,
            srvSup.group_name,
            srvSup.flexValue
          )
        );
      }
    }
    return mappedSuppliers;
  }

  blendSuppliers(
    mappedSuppliers: Supplier[],
    blend: GetSupplierBlendInterface
  ) {
    const blendSuppliers: BlendSupplier[] = [];
    blend.suppliers.forEach((blendSupp: BlendSupplier) => {
      for (const i in mappedSuppliers) {
        if (this.isSuppEqualBlendSupp(mappedSuppliers[i], blendSupp)) {
          blendSuppliers.push(blendSupp);
          break;
        }
      }
    });
    return blendSuppliers;
  }

  suppliersRemaning(
    mappedSuppliers: Supplier[],
    blend: GetSupplierBlendInterface
  ) {
    const supplierRemaining = [];
    for (const i in mappedSuppliers) {
      let notFound = true;
      blend.suppliers.forEach((blendSupp: BlendSupplier) => {
        if (this.isSuppEqualBlendSupp(mappedSuppliers[i], blendSupp)) {
          notFound = false;
        }
      });
      if (notFound) {
        supplierRemaining.push(i);
      }
    }
    return supplierRemaining;
  }

  setSuppliersToRedestribute(
    mappedSuppliers: Supplier[],
    blendSuppliers: BlendSupplier[],
    remainingCompletes: number,
    suppsIndexToRedistribute: number[],
    completes: number
  ) {
    for (const i in mappedSuppliers) {
      let notFound = true;
      let totalBlendAllocation = 0;
      mappedSuppliers[i].quantity = 0;
      blendSuppliers.forEach((blendSupp) => {
        if (this.isSuppEqualBlendSupp(mappedSuppliers[i], blendSupp)) {
          mappedSuppliers[i].quantity = Math.ceil(
            (Number(blendSupp.allocation_percentage) * completes) / 100
          );
          remainingCompletes = remainingCompletes - mappedSuppliers[i].quantity;
          notFound = false;
        }
        totalBlendAllocation += Number(blendSupp.allocation_percentage);
      });
      if (
        notFound &&
        this._isRemainingAllocationPercentage(totalBlendAllocation)
      ) {
        suppsIndexToRedistribute.push(Number(i));
      }
    }
    return remainingCompletes;
  }

  applyBlendAllocation(
    mappedSuppliers: Supplier[],
    blend: GetSupplierBlendInterface,
    completes: number
  ) {
    // eslint-disable-next-line prefer-const
    let remainingCompletes = completes;
    const suppsIndexToRedistribute: number[] = [];
    const blendSuppliers = this.blendSuppliers(mappedSuppliers, blend);
    remainingCompletes = this.setSuppliersToRedestribute(
      mappedSuppliers,
      blendSuppliers,
      remainingCompletes,
      suppsIndexToRedistribute,
      completes
    );
    this.distributeRemainingAllocations(
      suppsIndexToRedistribute,
      remainingCompletes,
      mappedSuppliers
    );
    this.setAllocationToElegibleSuppliers(
      suppsIndexToRedistribute,
      mappedSuppliers
    );
    return mappedSuppliers;
  }

  distributeRemainingAllocations(
    indexes: number[],
    remainingCompletes: number,
    mappedSuppliers: Supplier[]
  ) {
    if (this.validateRemainings(indexes.length, remainingCompletes)) {
      const allocationQty = 1;
      while (remainingCompletes > 0) {
        indexes.forEach((index) => {
          if (remainingCompletes > 0) {
            mappedSuppliers[index].quantity =
              mappedSuppliers[index].quantity + allocationQty;
            remainingCompletes = remainingCompletes - allocationQty;
          }
        });
      }
    }
  }

  validateRemainings(length: number, remainingCompletes: number) {
    return length > 0 && remainingCompletes > 0;
  }

  setAllocationToElegibleSuppliers(
    indexes: number[],
    mappedSuppliers: Supplier[]
  ) {
    indexes.forEach((index) => {
      if (mappedSuppliers[index].quantity === 0) {
        mappedSuppliers[index].quantity = 1;
      }
    });
  }

  isSuppEqualBlendSupp(mappedSupp: Supplier, blendSupp: BlendSupplier) {
    return (
      !this.isEmpty(blendSupp.allocation_percentage) &&
      (this.validateGroupSupp(mappedSupp, blendSupp) ||
        this.validateIndividualSupp(mappedSupp, blendSupp))
    );
  }

  validateGroupSupp(mappedSupp: Supplier, blendSupp: BlendSupplier) {
    return (
      mappedSupp.group_name && mappedSupp.group_name === blendSupp.group_name
    );
  }

  validateIndividualSupp(mappedSupp: Supplier, blendSupp: BlendSupplier) {
    if (typeof mappedSupp.suppliers[0] === 'object') {
      return (
        !mappedSupp.group_name &&
        mappedSupp.suppliers.length === 1 &&
        mappedSupp.suppliers[0].id === blendSupp.supplier_id[0].id
      );
    }
    return (
      !mappedSupp.group_name &&
      mappedSupp.suppliers.length === 1 &&
      mappedSupp.suppliers[0] === blendSupp.supplier_id[0]
    );
  }

  isEmpty(value: any) {
    return value === '' || value === undefined || value === null;
  }

  _isRemainingAllocationPercentage(value: number) {
    return value < 100;
  }
}
